import { ArrowUpRight } from 'lucide-react';
import CmsImage from '@/components/ui/CmsImage';
import { cn } from '@/utils/cn';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { GroupCompanyItem } from '@/types/group-company';
import Image from 'next/image';

interface GroupCompanyCardProps {
  item: GroupCompanyItem;
  /** Row index — odd rows flip the image to the right in the Figma. */
  index: number;
}

function pickImageUrl(media: GroupCompanyItem['itemImage']): string | null {
  if (!media) return null;
  return resolveStrapiMediaUrl(media.url);
}

export default function GroupCompanyCard({ item, index }: GroupCompanyCardProps) {
  const imageOnLeft = index % 2 === 0;
  const imageUrl = pickImageUrl(item.itemImage);
  const imageAlt = item.itemImage?.alternativeText || item.title;
  const iconUrl = item.groupIcon?.url ? resolveStrapiMediaUrl(item.groupIcon.url) : null;
  const iconAlt = item.groupIcon?.alternativeText || `${item.title} logo`;
  const iconWidth = item.groupIcon?.width ?? 296;
  const iconHeight = item.groupIcon?.height ?? 64;

  const primaryButton = item.button?.[0];
  const buttonHref = primaryButton?.buttonUrl?.trim() || null;

  return (
    <article
      className={cn(
        'grid grid-cols-1 overflow-hidden rounded-2xl bg-white md:grid-cols-2',
        !imageOnLeft && 'md:[&>:first-child]:order-2',
      )}
    >
      {/* Image panel */}
      <div className="relative aspect-square w-full bg-gray-100">
        <CmsImage
          src={imageUrl}
          alt={imageAlt}
          fill
          sizes="(max-width: 768px) 100vw, 50vw"
          className="object-cover"
          priority={index === 0}
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
      </div>

      {/* Content panel */}
      <div className="flex flex-col justify-center gap-6 p-8 md:p-12">
        {iconUrl && (
          <Image
            src={iconUrl}
            alt={iconAlt}
            width={iconWidth}
            height={iconHeight}
            className="h-12 w-auto md:h-16"
          />
        )}

        <div className="space-y-2">
          <h2 className="text-3xl font-medium leading-tight text-[#0a0a0a] md:text-5xl">
            {item.title}
          </h2>
          {item.subHeading && (
            <p className="text-xl font-normal text-[#0a0a0a] md:text-[32px] md:leading-[1.21]">
              {item.subHeading}
            </p>
          )}
        </div>

        <div
          className="prose prose-base max-w-none text-[#0a0a0a] prose-p:mt-0 prose-ul:mt-2 prose-li:my-1"
          dangerouslySetInnerHTML={{ __html: item.content }}
        />

        {(() => {
          if (primaryButton && buttonHref) {
            return (
              <a
                href={buttonHref}
                {...getCmsButtonLinkProps(
                  primaryButton,
                  /^https?:/i.test(buttonHref) ? '_blank' : undefined,
                )}
                aria-label={primaryButton.ariaLabel || primaryButton.buttonText}
                className="group inline-flex w-fit items-center gap-2 rounded-lg bg-[#ed1c22] px-6 py-3 text-base font-medium text-white transition-colors hover:bg-[#c80b0f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ed1c22] focus-visible:ring-offset-2"
              >
                {primaryButton.buttonText}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            );
          }
          if (primaryButton) {
            return (
              <span
                aria-disabled="true"
                className="inline-flex w-fit cursor-not-allowed items-center gap-2 rounded-lg bg-[#ed1c22]/70 px-6 py-3 text-base font-medium text-white"
                title="Link coming soon"
              >
                {primaryButton.buttonText}
                <ArrowUpRight className="h-4 w-4" />
              </span>
            );
          }
          return null;
        })()}
      </div>
    </article>
  );
}
